import { autoinject } from "aurelia-framework";
import { every } from 'lodash';
import { ApiService } from "../../utils/api-service";
import { json } from "aurelia-fetch-client";
import { CustomerService } from "./customer-service";
import {
	AttachedDataItem,
	IAttachedDataItemBase,
	ReportedServiceDataItemType
} from "../reported-service-data-items/reported-service-data-item-models";

export interface IServiceDataItems {
	[key: string]: AttachedDataItem[]; // key: serviceId
}

export interface IServiceDataItemsBase {
	[key: string]: IAttachedDataItemBase[]; // key: serviceId
}

export class DataItemOption {
	id: string;
	code: string;
	name: string;
	specifier: string;
	unit: string;
	description: string;
	type: ReportedServiceDataItemType;

	constructor(init?: Partial<DataItemOption>) {
		Object.assign(this, init);
	}
}

// Represents the to-be-added new item (measure/perf-indicator)
export class DataItemSelectionResult {
	serviceId: string;
	dataItemId: string;
	type: ReportedServiceDataItemType;

	constructor(init?: Partial<DataItemSelectionResult>) {
		Object.assign(this, init);
	}
}

export class DataItemSort {
	constructor(public id: string, public sortOrder: number) {}
}

@autoinject
export class ReportedServiceDataItemService {
	constructor(
		private api: ApiService,
		private customerService: CustomerService
	) {}

	getReportedServiceDataItemsForService(
		serviceId: string,
		customerId: string,
		scopeTypeGroupId: string
	): Promise<AttachedDataItem[]> {
		return this.api.fetchJson(
			`services/${serviceId}/customers/${customerId}/reported-service-data-items?scopeTypeGroupId=${scopeTypeGroupId}`
		);
	}

	getReportedServiceDataItemsForServiceStructure(
		scopeTypeGroupId: string,
		serviceStructureId: string
	): Promise<IServiceDataItems> {
		const customerId = this.customerService.selectedCustomerId;

		return this.api.fetchJson(
			`service-structures/${serviceStructureId}/reported-service-data-items/by-customer/${customerId}?scopeTypeGroupId=${scopeTypeGroupId}`
		);
	}

	addReportedServiceDataItem(
		item: AttachedDataItem,
		customerId: string
	): Promise<any> {
		item.dataItemName = null;
		return this.api.fetchJson(
			`services/${item.serviceId}/customers/${customerId}/reported-service-data-items/for-scopetypegroup/${item.scopeTypeGroupId}`,
			{
				method: "post",
				body: json(item)
			}
		);
	}

	updateReportedServiceDataItemInputType(
		serviceId: string,
		itemId: string,
		inputType: string,
		isCollected: boolean
	) {
		const customerId = this.customerService.selectedCustomerId;

		return this.api.fetch(
			`services/${serviceId}/customers/${customerId}/reported-service-data-items/${itemId}` +
				`/${inputType}/${isCollected}`,
			{
				method: "post"
			}
		);
	}

	removeReportedServiceDataItem(item: AttachedDataItem) {
		const customerId = this.customerService.selectedCustomerId;

		const url = `services/${
			item.serviceId
		}/customers/${customerId}/reported-service-data-items/remove/${
			item.id
		}`;

		return this.api.fetch(url, {
			method: "delete"
		});
	}

	async changeDataItemDescription(
		reportedServiceDataItem: AttachedDataItem,
		customerId: string
	): Promise<any> {
		const url = `services/${
			reportedServiceDataItem.serviceId
		}/customers/${customerId}/reported-service-data-items/change-description/${
			reportedServiceDataItem.id
		}`;

		return await this.api.fetch(url, {
			method: "post",
			body: json(reportedServiceDataItem.description)
		});
	}

	async updateOrderNumbers(
		serviceId: string,
		customerId: string,
		items: AttachedDataItem[]
	): Promise<any> {
		const url = customerId
			? `services/${serviceId}/customers/${customerId}/reported-service-data-items/order-numbers`
			: `services/${serviceId}/default-reported-service-data-items/order-numbers`;

		let sortItems = items.map(i => new DataItemSort(i.id, i.sortOrder));

		return await this.api.fetch(url, {
			method: "post",
			body: json(sortItems)
		});
	}

	async addScopeTypeId(
		reportedDataItem: AttachedDataItem,
		customerId: string,
		scopeTypeid: string
	): Promise<Response> {
		const url =
			`services/${reportedDataItem.serviceId}/customers/${customerId}/` +
			`reported-service-data-items/${
				reportedDataItem.id
			}/add-scopetype/${scopeTypeid}`;

		return this.api.fetch(url, {
			method: "post"
		});
	}

	async removeScopeTypeId(
		reportedDataItem: AttachedDataItem,
		customerId: string,
		scopeTypeid: string
	): Promise<Response> {
		const url =
			`services/${reportedDataItem.serviceId}/customers/${customerId}/` +
			`reported-service-data-items/${
				reportedDataItem.id
			}/remove-scopetype/${scopeTypeid}`;

		return this.api.fetch(url, {
			method: "post"
		});
	}

	public static error_CannotRemoveLastInputFromCustomerComparisonMeasure: string = "CannotRemoveLastInputFromCustomerComparisonMeasure";

	isRemovingLastInputType(
		dataItem: AttachedDataItem,
		inputType: string,
		newIsCollected: boolean): boolean {

		if (newIsCollected) { return false; } // is adding, not removing

		const inputTypes: { [key: string]: boolean } = {};
		inputTypes['forCostCentersByMunicipalities'] = dataItem.forCostCentersByMunicipalities;
		inputTypes['forCostCentersTotal'] = dataItem.forCostCentersTotal;
		inputTypes['forServiceByMunicipalities'] = dataItem.forServiceByMunicipalities;
		inputTypes['forServiceTotal'] = dataItem.forServiceTotal;

		inputTypes[inputType] = newIsCollected;

		return every(inputTypes, y => !y);
	}
}
